import React, { useState } from 'react';
import { X, Send, Bell, AlertCircle, MessageSquare, Gift, Info } from 'lucide-react';
import { COLORS } from '../../../../utils/constants';
import { adminAPI } from '../../../../services/api';
import { useAdminToast } from '../../../../hooks/useAdminToast';

const SendNotificationModal = ({ clientId, clientEmail, onClose, onSuccess }) => {
  const { toast } = useAdminToast();
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [notificationType, setNotificationType] = useState('system');
  const [loading, setLoading] = useState(false);

  const notificationTypes = [
    { id: 'system', label: 'Системное', icon: Info, color: '#3b82f6' },
    { id: 'alert', label: 'Важное', icon: AlertCircle, color: COLORS.danger },
    { id: 'news', label: 'Новость', icon: MessageSquare, color: COLORS.success },
    { id: 'promo', label: 'Акция', icon: Gift, color: '#f59e0b' },
  ];

  const quickTemplates = [
    { title: 'Верификация', message: 'Пожалуйста, пройдите верификацию аккаунта для полного доступа к функциям платформы.' },
    { title: 'Депозит получен', message: 'Ваш депозит успешно зачислен на баланс. Спасибо за использование нашей платформы!' },
    { title: 'Вывод обработан', message: 'Ваш запрос на вывод средств обработан и отправлен на указанный адрес.' },
    { title: 'Специальное предложение', message: 'Для вас доступно специальное предложение. Свяжитесь с менеджером для деталей.' },
  ];

  const handleSubmit = async () => {
    if (!title.trim() || !message.trim()) return;
    if (!clientId) {
      toast.error('Ошибка', 'ID клиента не указан');
      return;
    }
    setLoading(true);
    try {
      await adminAPI.sendClientNotification(clientId, title, message, notificationType);
      toast.success('Отправлено', 'Уведомление успешно отправлено');
      onSuccess();
    } catch (err) {
      console.error('Send notification error:', err);
      toast.apiError(err, 'Не удалось отправить уведомление');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div 
      style={{ 
        position: 'fixed', 
        inset: 0, 
        backgroundColor: 'rgba(0,0,0,0.8)', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        zIndex: 1100 
      }} 
      onClick={onClose}
    >
      <div 
        style={{ 
          backgroundColor: COLORS.card, 
          borderRadius: '16px', 
          padding: '24px', 
          width: '100%', 
          maxWidth: '480px',
          maxHeight: '90vh',
          overflowY: 'auto' 
        }} 
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{ 
              width: '40px', 
              height: '40px', 
              borderRadius: '10px', 
              backgroundColor: `${COLORS.primary}20`, 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center' 
            }}>
              <Send size={20} color={COLORS.primary} />
            </div>
            <div>
              <h3 style={{ color: COLORS.text, fontSize: '18px', fontWeight: '700', margin: 0 }}>Отправить уведомление</h3>
              <div style={{ color: COLORS.textSecondary, fontSize: '12px' }}>{clientEmail}</div>
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: COLORS.textSecondary, cursor: 'pointer' }}>
            <X size={20} />
          </button>
        </div>

        {/* Notification Type */}
        <div style={{ marginBottom: '16px' }}>
          <label style={{ display: 'block', color: COLORS.textSecondary, fontSize: '13px', marginBottom: '8px' }}>Тип уведомления</label>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px' }}>
            {notificationTypes.map(type => {
              const Icon = type.icon;
              const isActive = notificationType === type.id;
              return (
                <button
                  key={type.id}
                  onClick={() => setNotificationType(type.id)}
                  style={{
                    padding: '10px 8px',
                    backgroundColor: isActive ? `${type.color}20` : COLORS.background,
                    border: `2px solid ${isActive ? type.color : COLORS.border}`,
                    borderRadius: '8px',
                    cursor: 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '4px',
                  }}
                >
                  <Icon size={18} color={isActive ? type.color : COLORS.textSecondary} />
                  <span style={{ fontSize: '11px', color: isActive ? type.color : COLORS.textSecondary }}>{type.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Quick Templates */}
        <div style={{ marginBottom: '16px' }}>
          <label style={{ display: 'block', color: COLORS.textSecondary, fontSize: '13px', marginBottom: '8px' }}>Быстрые шаблоны</label>
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
            {quickTemplates.map((template, idx) => (
              <button
                key={idx}
                onClick={() => { setTitle(template.title); setMessage(template.message); }}
                style={{
                  padding: '6px 12px',
                  backgroundColor: COLORS.background,
                  border: `1px solid ${COLORS.border}`,
                  borderRadius: '16px',
                  color: COLORS.textSecondary,
                  fontSize: '11px',
                  cursor: 'pointer',
                }}
              >
                {template.title}
              </button>
            ))}
          </div>
        </div>

        {/* Title */}
        <div style={{ marginBottom: '12px' }}>
          <label style={{ display: 'block', color: COLORS.textSecondary, fontSize: '13px', marginBottom: '6px' }}>Заголовок</label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Введите заголовок..."
            style={{
              width: '100%',
              padding: '12px',
              backgroundColor: COLORS.background,
              border: `1px solid ${COLORS.border}`,
              borderRadius: '8px',
              color: COLORS.text,
              fontSize: '14px',
              boxSizing: 'border-box',
            }}
          />
        </div>

        {/* Message */}
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', color: COLORS.textSecondary, fontSize: '13px', marginBottom: '6px' }}>Сообщение</label>
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Текст уведомления..."
            rows={4}
            style={{
              width: '100%',
              padding: '12px',
              backgroundColor: COLORS.background,
              border: `1px solid ${COLORS.border}`,
              borderRadius: '8px',
              color: COLORS.text,
              fontSize: '14px',
              resize: 'vertical',
              boxSizing: 'border-box',
            }}
          />
        </div>

        {/* Preview */}
        {(title || message) && (
          <div style={{
            padding: '12px',
            backgroundColor: COLORS.background,
            borderRadius: '10px',
            marginBottom: '20px',
            border: `1px solid ${COLORS.border}`,
          }}>
            <div style={{ color: COLORS.textSecondary, fontSize: '11px', marginBottom: '8px' }}>Предпросмотр:</div>
            <div style={{ 
              padding: '12px', 
              backgroundColor: COLORS.card, 
              borderRadius: '8px',
              borderLeft: `3px solid ${notificationTypes.find(t => t.id === notificationType)?.color || COLORS.primary}`,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                <Bell size={14} color={notificationTypes.find(t => t.id === notificationType)?.color} />
                <span style={{ color: COLORS.text, fontSize: '14px', fontWeight: '600' }}>{title || 'Заголовок'}</span>
              </div>
              <div style={{ color: COLORS.textSecondary, fontSize: '13px' }}>{message || 'Текст сообщения...'}</div>
            </div>
          </div>
        )}

        {/* Submit Button */}
        <button
          onClick={handleSubmit}
          disabled={loading || !title.trim() || !message.trim()}
          style={{
            width: '100%',
            padding: '14px',
            backgroundColor: loading || !title.trim() || !message.trim() ? COLORS.border : COLORS.primary,
            border: 'none',
            borderRadius: '8px',
            color: '#000',
            fontSize: '15px',
            fontWeight: '600',
            cursor: loading || !title.trim() ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
          }}
        >
          <Send size={18} />
          {loading ? 'Отправка...' : 'Отправить уведомление'}
        </button>
      </div>
    </div>
  );
};

export default SendNotificationModal;
